import { createSlice } from "@reduxjs/toolkit";
import ToasterService from "utils/toaster.util";
import { NewPricingState } from ".";
import { PayloadAction } from "@reduxjs/toolkit";
import { config } from "config";
import { SelectOption } from "components/atoms/Select";


const default_page_size = config.PAGE_SIZE;
const initialState: NewPricingState = {
    pricings: [],
    pricing: null,
    loading: true,
    pricingsByZone: {},
    pricingsByZoneAndEngine: {},
    zoneOptions: [],
    priceClassesByZone: {},
    priceClassesOptions: [],
    refresh: 0,
    refreshLoader: false,
    current_filters: {},
    filters: { page: 1, page_size: default_page_size },
};


export const newPricingSlice = createSlice({
    name: "newPricing",
    initialState,
    reducers: {
        clearPricingAssign: (state) => {
            state.zoneOptions = [];
            state.priceClassesOptions = [];
        },
        clearPricing: (state) => {
            state.pricings = [];
            // state.pricing=null
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        addPricing: (state, action) => {
            let pricingsByZone: any = {};
            let pricings = state.pricings;
            pricings.unshift(action.payload);
            pricings.forEach((pricing: any) => {
                const { city_id } = pricing;
                let zone_name = city_id?._id || "Undefined";
                if (pricingsByZone[zone_name])
                    pricingsByZone[zone_name].pricings.push(pricing);
                else
                    pricingsByZone[zone_name] = {
                        pricings: [pricing],
                        zone_name: city_id?.name,
                    };
            });
            state.pricings = pricings;
            state.pricingsByZone = pricingsByZone;
        },
        updatePricing: (state, action) => {
            const { id, pricing } = action.payload;
            let pricingsByZone = state.pricingsByZone;
            state.pricings.every(({ _id }: { _id: string }, i: number) => {
                if (id === _id) {
                    state.pricings[i] = pricing;
                    return false;
                }
                return true;
            });
            for (const city in pricingsByZone) {
                if (Object.prototype.hasOwnProperty.call(pricingsByZone, city)) {
                    let done = false;
                    let element = pricingsByZone[city];
                    element.pricings.every(({ _id }: { _id: string }, i: number) => {
                        if (id === _id) {
                            done = true;
                            element.pricings[i] = pricing;
                            return false;
                        }
                        return true;
                    });
                    if (done) break;
                }
            }
        },
        setPricing: (state, action) => {
            state.pricing = action.payload;
        },
        setPricingByClass: (state, action) => {
            let class_id = action.payload;

            // Clear first. Without this a class that has no pricing row silently
            // kept the previously selected class' pricing — so switching to a
            // company whose corporate set lacks the class priced the booking off
            // the wrong record, and the warning below never fired.
            state.pricing = null;

            for (const pricing of state.pricings) {
                if (pricing.class_id._id === class_id) {
                    state.pricing = pricing;
                    break;
                }
            }

            if (!state.pricing)
                ToasterService.showError("Selected Class has no pricing yet!");
        },
        setPricings: (state, action) => {
            let pricingsByZone: any = {};
            let pricingsByZoneAndEngine: any = {};
            const pricings = action.payload;

            pricings.forEach((pricing: any) => {
                const { zone_id, type, price_engine_id } = pricing;
                
                if (zone_id) {
                    // First level: Group by zone
                    const zoneKey = `${zone_id._id}_${type}`;
                    
                    if (!pricingsByZoneAndEngine[zoneKey]) {
                        pricingsByZoneAndEngine[zoneKey] = {
                            zone_name: zone_id.name,
                            zone: zone_id,
                            type,
                            price_engines: {} // Changed from 'engines' to 'price_engines' for clarity
                        };
                    }

                    // Second level: Group by price engine within zone
                    if (price_engine_id) {
                        const engineKey = price_engine_id._id;
                        
                        if (!pricingsByZoneAndEngine[zoneKey].price_engines[engineKey]) {
                            pricingsByZoneAndEngine[zoneKey].price_engines[engineKey] = {
                                pricings: [],
                                engine_name: price_engine_id.name,
                                engine: price_engine_id
                            };
                        }

                        pricingsByZoneAndEngine[zoneKey].price_engines[engineKey].pricings.push(pricing);
                    }
                }

                // Keep the old grouping for backward compatibility
                let zone_name = zone_id?._id;
                const key = `${zone_name}_${type}`;
                if (pricingsByZone[key]) {
                    pricingsByZone[key].pricings.push(pricing);
                } else {
                    pricingsByZone[key] = {
                        pricings: [pricing],
                        zone_name: zone_id?.name,
                        zone: zone_id,
                        type,
                    };
                }
            });
            
            state.pricings = pricings;
            state.refreshLoader = false;
            state.pricingsByZone = pricingsByZone;
            state.pricingsByZoneAndEngine = pricingsByZoneAndEngine;
        },
        setPriceClasses: (state, action) => {
            let classesByZone: any = {};
            const classes = action.payload;
            classes.forEach((classs: any) => {
                const { zone_id } = classs;
                let zone_name = zone_id?._id;

                if (classesByZone[zone_name])
                    classesByZone[zone_name].classes.push(classs);
                else
                    classesByZone[zone_name] = {
                        models_length: 0,
                        classes: [classs],
                        zone_name: zone_id?.name,
                    };
            });

            state.priceClassesByZone = classesByZone;
        },
        setPriceZones: (state, action) => {
            const zones = action.payload;
            let options: SelectOption[] = [];
            zones.forEach(({ _id, name }: any) => options.push({ value: _id, label: name }));
            state.zoneOptions = options;
        },
        setPriceClassesOptionsByZoneId: (state, action) => {
            const zone_id = action.payload;
            const classes = state.priceClassesByZone?.[zone_id]?.["classes"] || [];
            state.priceClassesOptions = classes.map(({ _id, name }: any) => ({
                value: _id,
                label: name,
            }));
        },
        setPricingsLocal: (state, action) => {
            const { payload } = action;
            state.pricingsByZoneAndEngine = payload;
        },
        refresh: (state) => {
            state.refresh += 1;
            state.refreshLoader = true;
        },
        resetPage: (state) => {
            state.filters.page = 1;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
            state.refreshLoader = true;
        },
        setCurrentFilters: (state, action) => {
            state.current_filters = action.payload;
        },
        resetFilters: (state) => {
            state.refresh += 1;
            state.refreshLoader = true;
            state.filters = initialState.filters;
            state.current_filters = initialState.current_filters;
        },
    }
});

const newPricingReducer = newPricingSlice.reducer;

export const newPricingActions = newPricingSlice.actions;
export default newPricingReducer; 